import { useEffect, useRef, useState, forwardRef } from 'react';
import { Check, ChevronDown, Search } from 'lucide-react';
import { cn } from '../../lib/cn';

export const Select = forwardRef(function Select(
  {
    className,
    value,
    onChange,
    options = [],
    placeholder = 'Selecione...',
    disabled,
    invalid,
    searchable = false,
    onSearch,
    leftIcon: LeftIcon,
    renderOption,
    style,
    ...props
  },
  ref
) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const triggerRef = useRef(null);
  const popoverRef = useRef(null);

  useEffect(() => {
    if (!open) {
      setQuery('');
      return;
    }
    function onClickOutside(e) {
      if (
        !triggerRef.current?.contains(e.target) &&
        !popoverRef.current?.contains(e.target)
      ) {
        setOpen(false);
      }
    }
    function onEsc(e) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onClickOutside);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onClickOutside);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open]);

  const selected = options.find((o) => o.value === value);
  const filtered = searchable && !onSearch
    ? options.filter((o) =>
        o.label?.toString().toLowerCase().includes(query.toLowerCase())
      )
    : options;

  return (
    <div className={cn('relative w-full', className)} ref={ref}>
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        className={cn(
          'flex h-11 w-full items-center justify-between gap-2 rounded-xl text-left text-sm transition-all duration-200',
          LeftIcon ? 'pl-10' : 'pl-4',
          'pr-3',
          disabled && 'cursor-not-allowed opacity-50'
        )}
        style={{
          background: 'var(--dash-surface-subtle)',
          color: selected ? 'var(--dash-text)' : 'var(--dash-text-faint)',
          border: invalid
            ? '1px solid rgba(254,52,86,0.6)'
            : open
              ? '1px solid var(--dash-primary)'
              : '1px solid var(--dash-border)',
          boxShadow: open ? '0 0 0 4px rgba(173,52,254,0.12)' : 'none',
          ...style,
        }}
        {...props}
      >
        {LeftIcon && (
          <LeftIcon
            size={15}
            strokeWidth={2}
            className="absolute left-3.5 top-1/2 -translate-y-1/2"
            style={{ color: open ? 'var(--dash-primary)' : 'var(--dash-text-muted)' }}
          />
        )}
        <span className="flex-1 truncate">
          {selected ? selected.label : placeholder}
        </span>
        <ChevronDown
          size={15}
          className="shrink-0 transition-transform"
          style={{
            color: 'var(--dash-text-muted)',
            transform: open ? 'rotate(180deg)' : 'rotate(0)',
          }}
        />
      </button>

      {open && (
        <div
          ref={popoverRef}
          className="absolute left-0 right-0 z-50 mt-2 overflow-hidden rounded-xl"
          style={{
            background: 'var(--dash-surface)',
            border: '1px solid var(--dash-border-strong)',
            boxShadow:
              '0 24px 48px -12px rgba(0,0,0,0.35), 0 0 0 1px var(--dash-border)',
          }}
        >
          {searchable && (
            <div
              className="relative border-b"
              style={{ borderColor: 'var(--dash-border)' }}
            >
              <Search
                size={13}
                className="absolute left-3 top-1/2 -translate-y-1/2"
                style={{ color: 'var(--dash-text-muted)' }}
              />
              <input
                autoFocus
                value={query}
                onChange={(e) => {
                  const v = e.target.value;
                  setQuery(v);
                  onSearch?.(v);
                }}
                placeholder="Buscar..."
                className="h-9 w-full bg-transparent pl-9 pr-3 text-xs outline-none"
                style={{ color: 'var(--dash-text)' }}
              />
            </div>
          )}
          <div className="dash-scroll max-h-64 overflow-y-auto py-1">
            {filtered.length === 0 && (
              <div
                className="px-4 py-3 text-center text-xs"
                style={{ color: 'var(--dash-text-faint)' }}
              >
                Nenhuma opção encontrada
              </div>
            )}
            {filtered.map((opt) => {
              const isSelected = opt.value === value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => {
                    onChange?.(opt.value);
                    setOpen(false);
                  }}
                  className={cn(
                    'flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm transition-colors'
                  )}
                  style={{
                    background: isSelected
                      ? 'var(--dash-primary-soft)'
                      : 'transparent',
                    color: isSelected
                      ? 'var(--dash-primary)'
                      : 'var(--dash-text)',
                  }}
                  onMouseEnter={(e) => {
                    if (!isSelected)
                      e.currentTarget.style.background =
                        'var(--dash-surface-hover)';
                  }}
                  onMouseLeave={(e) => {
                    if (!isSelected)
                      e.currentTarget.style.background = 'transparent';
                  }}
                >
                  <span className="flex-1 truncate">
                    {renderOption ? renderOption(opt) : opt.label}
                  </span>
                  {isSelected && <Check size={14} strokeWidth={2.5} />}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
});
